import React, { useMemo } from 'react';
import { CheckCircle2, XCircle, Clock, Users, Circle } from 'lucide-react';
import { Chess } from 'chess.js';

export default function ChallengeResponsesPanel({ room, inline = false }) {
  const ae = room?.active_explanation;

  const data = useMemo(() => {
    const students = (room?.participants || []).filter((p) => p.role === 'student');
    const steps = ae?.activity?.steps;
    const cur = Number(ae?.current_step) || 0;
    const step = Array.isArray(steps) ? steps[cur] : null;
    const mode = step?.mode;

    if (mode !== 'challenge' && mode !== 'explore') {
      return { mode: null, rows: [], total: 0, answered: 0 };
    }

    const stepId = String(step?.id || `step-${cur}`);

    if (mode === 'challenge') {
      const responses = ae?.challenge_responses?.[stepId] || {};
      const rows = students.map((s) => ({
        user_id: s.user_id,
        name: s.name || 'Alumno',
        status: responses[s.user_id]?.status || 'pending',
      }));
      return {
        mode,
        rows,
        total: rows.length,
        answered: rows.filter((r) => r.status === 'correct' || r.status === 'incorrect').length,
      };
    }

    const explore = step?.explore || {};
    let total = Array.isArray(explore.targets)
      ? new Set(explore.targets.map((x) => String(x).toLowerCase())).size
      : 0;

    if (explore.type === 'discover') {
      try {
        const chess = new Chess(step?.board?.fen || undefined);
        const square = String(explore.piece_square || '').toLowerCase();
        const piece = chess.get(square);
        if (piece) {
          const fields = chess.fen().split(' ');
          fields[1] = piece.color;
          chess.load(fields.join(' '));
          total = new Set(chess.moves({ square, verbose: true }).map((m) => m.to)).size;
        } else {
          total = 0;
        }
      } catch {
        total = 0;
      }
    }

    const progress = ae?.explore_progress?.[stepId] || {};
    const rows = students.map((s) => {
      const saved = progress[s.user_id];
      const completed = saved ? Number(saved.completed) || 0 : 0;
      const rowTotal = saved ? Number(saved.total) || total : total;
      const done = rowTotal > 0 && completed >= rowTotal;
      return {
        user_id: s.user_id,
        name: s.name || 'Alumno',
        completed: Math.min(completed, rowTotal),
        total: rowTotal,
        status: done ? 'completed' : completed > 0 ? 'in_progress' : 'pending',
      };
    });

    return {
      mode,
      rows,
      total: rows.length,
      answered: rows.filter((r) => r.status === 'completed').length,
    };
  }, [room?.participants, ae]);

  if (!data.mode) return null;
  const isExplore = data.mode === 'explore';

  return (
    <div
      data-testid="challenge-responses-panel"
      className={inline ? "w-full min-w-0 rounded-xl bg-white shadow-xl border border-slate-200 overflow-hidden" : "fixed z-[60] pointer-events-auto right-3 top-24 w-40 max-w-[calc(100vw-1.5rem)] rounded-xl bg-white shadow-xl border border-slate-200 overflow-hidden"}
    >
      <div className="flex items-center gap-1.5 px-2.5 py-2 bg-slate-900 text-white">
        <Users className="w-3.5 h-3.5 shrink-0" />
        <span className="text-xs font-bold">{isExplore ? 'Exploración' : 'Respuestas'}</span>
      </div>
      <ul className="max-h-[35vh] overflow-y-auto divide-y divide-slate-100">
        {data.rows.length === 0 && (
          <li className="px-2.5 py-2 text-[11px] text-slate-400">Aún no hay alumnos.</li>
        )}
        {data.rows.map((r) => (
          <li key={r.user_id} data-testid={`crp-student-${r.user_id}`}
            className="flex items-center justify-between gap-1.5 px-2.5 py-1.5">
            <span className="min-w-0 truncate text-xs text-slate-700">{r.name}</span>
            {isExplore ? (
              <span className="shrink-0 flex items-center gap-1 text-[11px] font-semibold tabular-nums">
                <span>{r.completed}/{r.total}</span>
                {r.status === 'completed'
                  ? <CheckCircle2 aria-label="Completado" title="Completado" className="h-3.5 w-3.5 text-emerald-600" />
                  : r.status === 'in_progress'
                    ? <Clock aria-label="En progreso" title="En progreso" className="h-3.5 w-3.5 text-blue-500" />
                    : <Circle aria-label="Pendiente" title="Pendiente" className="h-3.5 w-3.5 text-slate-300" />}
              </span>
            ) : (
              r.status === 'correct'
                ? <CheckCircle2 aria-label="Correcto" title="Correcto" className="h-4 w-4 shrink-0 text-emerald-600" />
                : r.status === 'incorrect'
                  ? <XCircle aria-label="Incorrecto" title="Incorrecto" className="h-4 w-4 shrink-0 text-rose-600" />
                  : <Clock aria-label="Pendiente" title="Pendiente" className="h-4 w-4 shrink-0 text-amber-500" />
            )}
          </li>
        ))}
      </ul>
      <div className="border-t border-slate-100 px-2.5 py-1.5 text-[11px] font-bold tabular-nums text-slate-700">
        {isExplore
          ? `${data.answered} / ${data.total} completaron`
          : `${data.answered} / ${data.total} respondieron`}
      </div>
    </div>
  );
}
